import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CottageOutlinedIcon from "@mui/icons-material/CottageOutlined";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import MapsHomeWorkOutlinedIcon from "@mui/icons-material/MapsHomeWorkOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SavingsIcon from "@mui/icons-material/Savings";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { useAuth } from "../../contexts/AuthContext";

const drawerWidth = 280;
const drawerWidthCollapsed = 92;

const mainItems = [
  {
    icon: <Groups2OutlinedIcon />,
    label: "Contacts",
    path: "/contacts",
  },
  {
    icon: <MapsHomeWorkOutlinedIcon />,
    label: "A vendre",
    path: "/houseforsale",
  },
  {
    icon: <CottageOutlinedIcon />,
    label: "Location",
    path: "/housetorent",
  },
  {
    icon: <ListAltOutlinedIcon />,
    label: "Liste des ventes",
    path: "/sales",
  },
  {
    icon: <SavingsOutlinedIcon />,
    label: "Commissions",
    path: "/salaire",
  },
];

const configurationItems = [
  {
    icon: <StorefrontIcon />,
    label: "Agences",
    path: "/configuration/agences",
  },
  {
    icon: <PeopleAltIcon />,
    label: "Salaries",
    path: "/configuration/users",
  },
  {
    icon: <SavingsIcon />,
    label: "Seuils de com.",
    path: "/configuration/commissions",
    sx: {
      mt: 0.5,
    },
  },
  {
    icon: <ImportExportIcon />,
    label: "Import XLS",
    path: "/import-xls",
  },
];

function NavigationItem({ item, compact = false, nested = false }) {
  return (
    <ListItemButton
      component={NavLink}
      to={item.path}
      sx={{
        borderRadius: 1,
        mb: 0.75,
        ml: nested ? 1.5 : 0,
        minHeight: 50,
        px: compact ? 1.25 : 1.5,
        border: "1px solid transparent",
        justifyContent: compact ? "center" : "flex-start",
        "&.active": {
          background:
            "linear-gradient(135deg, rgba(240, 93, 143, 0.16) 0%, rgba(243, 154, 71, 0.16) 100%)",
          color: "#ffffff",
          borderColor: alpha("#ffffff", 0.16),
          "& .MuiListItemIcon-root": {
            color: "secondary.light",
          },
        },
        "&:hover": {
          backgroundColor: alpha("#ffffff", 0.05),
        },
        ...item.sx,
      }}
    >
      <ListItemIcon
        sx={{
          color: "inherit",
          minWidth: compact ? 0 : 40,
          justifyContent: "center",
        }}
      >
        {item.icon}
      </ListItemIcon>
      {!compact && <ListItemText primary={item.label} />}
    </ListItemButton>
  );
}

export default function AppLayout() {
  const [menuOpen, setMenuOpen] = useState(true);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const { logout } = useAuth();

  const compact = !menuOpen;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <Drawer
        variant="permanent"
        PaperProps={{
          sx: {
            width: compact ? drawerWidthCollapsed : drawerWidth,
            overflowX: "hidden",
            border: 0,
            p: 2.5,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            backgroundImage:
              "linear-gradient(180deg, rgba(12,12,12,0.98) 0%, rgba(20,20,20,1) 100%)",
            transition: (theme) =>
              theme.transitions.create("width", {
                duration: theme.transitions.duration.standard,
              }),
          },
        }}
        sx={{
          width: compact ? drawerWidthCollapsed : drawerWidth,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          {!compact && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                component="img"
                src="/brand/logo-original.svg"
                alt="AloapSoftware"
                sx={{ width: "auto", height: 40, filter: "brightness(0) invert(1)", flexShrink: 0 }}
              />
              <Typography variant="h5" sx={{ lineHeight: 1 }}>
                IMMOCOM
              </Typography>
            </Stack>
          )}
          <IconButton
            onClick={() => setMenuOpen((current) => !current)}
            sx={{
              color: "inherit",
              border: "1px solid",
              borderColor: alpha("#fff", 0.12),
            }}
          >
            <MenuIcon />
          </IconButton>
        </Stack>

        <List sx={{ p: 0 }}>
          {mainItems.map((item) => (
            <NavigationItem key={item.path} item={item} compact={compact} />
          ))}
        </List>

        <Divider sx={{ my: 2, borderColor: alpha("#fff", 0.12) }} />

        <List sx={{ p: 0 }}>
          <ListItemButton
            onClick={() => setConfigurationOpen((current) => !current)}
            sx={{
              borderRadius: 1,
              minHeight: 50,
              px: compact ? 1.25 : 1.5,
              border: "1px solid transparent",
              justifyContent: compact ? "center" : "flex-start",
              "&:hover": {
                backgroundColor: alpha("#ffffff", 0.05),
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: compact ? 0 : 40,
                justifyContent: "center",
              }}
            >
              <SettingsOutlinedIcon />
            </ListItemIcon>
            {!compact && (
              <>
                <ListItemText primary="Configuration" />
                {configurationOpen ? <ExpandLess /> : <ExpandMore />}
              </>
            )}
          </ListItemButton>

          <Collapse in={!compact && configurationOpen} timeout="auto" unmountOnExit>
            <List sx={{ pt: 1 }}>
              {configurationItems.map((item) => (
                <NavigationItem key={item.path} item={item} nested />
              ))}
            </List>
          </Collapse>
        </List>

        <Box sx={{ flexGrow: 1 }} />

        <Divider sx={{ my: 2, borderColor: alpha("#fff", 0.12) }} />

        <List sx={{ p: 0 }}>
          <ListItemButton
            onClick={logout}
            sx={{
              borderRadius: 3,
              minHeight: 52,
              px: compact ? 1.5 : 2,
              justifyContent: compact ? "center" : "flex-start",
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: compact ? 0 : 40,
                justifyContent: "center",
              }}
            >
              <ExitToAppIcon />
            </ListItemIcon>
            {!compact && <ListItemText primary="Deconnexion" />}
          </ListItemButton>
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          p: 0,
          position: "relative",
          backgroundColor: "background.default",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(244,239,232,0.78) 100%), url('/brand/background.webp')",
            backgroundPosition: "center",
            backgroundSize: "cover",
            opacity: 0.6,
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "32vw",
            minWidth: 220,
            height: "100%",
            background:
              "linear-gradient(180deg, rgba(240,93,143,0.08) 0%, rgba(243,154,71,0.14) 55%, rgba(17,17,17,0) 100%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "100%",
            minHeight: "100vh",
            overflow: "hidden",
            bgcolor: "transparent",
            pl: { xs: 1.5, md: 2.5 },
            pr: { xs: 1, md: 1.5 },
            pt: { xs: 1, md: 1.5 },
            pb: { xs: 1, md: 1.5 },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
